import React, { useState } from 'react';
import {
  CheckCircle2,
  Wrench,
  CircleDashed,
  Tag,
  Database,
  GitBranch,
  Workflow,
  ListFilter,
  Mail,
  MessageSquare,
} from 'lucide-react';
import {
  GHL_COMPLETED,
  GHL_CUSTOM_FIELDS,
  GHL_DYNAMIC_TAGS,
  GHL_PIPELINES,
  GHL_WORKFLOWS,
  GHL_SMART_LISTS,
  GHL_EMAIL_ACTIVE,
  GHL_SMS_ACTIVE,
  ghlAllTags,
  ghlAllStages,
  ghlCounts,
  type GhlStatus,
  type GhlItem,
} from '@/data/ghlPlan';

/**
 * GOHIGHLEVEL IMPLEMENTATION CHECKLIST (admin only).
 *
 * Renders src/data/ghlPlan.ts, which itself derives its tag and stage lists
 * from src/lib/crmForms.ts — the same map the deployed crm-capture function
 * matches on. So this screen can never drift from what the platform actually
 * sends to the CRM.
 *
 * Legend: Live = running in production · Configure in GHL = the platform
 * already sends the trigger, a human must build the workflow · Not built yet.
 */

const STATUS_META: Record<GhlStatus, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  done: { label: 'Live', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', Icon: CheckCircle2 },
  manual: { label: 'Configure in GHL', cls: 'bg-amber-50 text-amber-700 ring-amber-200', Icon: Wrench },
  todo: { label: 'Not built yet', cls: 'bg-slate-100 text-slate-600 ring-slate-200', Icon: CircleDashed },
};

const StatusPill: React.FC<{ status: GhlStatus }> = ({ status }) => {
  const m = STATUS_META[status];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${m.cls}`}
    >
      <m.Icon className="h-3 w-3" /> {m.label}
    </span>
  );
};

const ItemRow: React.FC<{ item: GhlItem }> = ({ item }) => (
  <li className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-start justify-between gap-3">
      <p className="text-sm font-bold text-slate-900">{item.title}</p>
      <StatusPill status={item.status} />
    </div>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{item.detail}</p>
    {item.where && (
      <p className="mt-2 font-mono text-[11px] text-slate-400">{item.where}</p>
    )}
  </li>
);

const Card: React.FC<{
  id: string;
  title: string;
  Icon: typeof Tag;
  count?: number;
  children: React.ReactNode;
}> = ({ id, title, Icon, count, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <section id={id} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <h3 className="flex items-center gap-2 text-base font-extrabold text-slate-900">
          <Icon className="h-4 w-4 text-orange-500" /> {title}
          {typeof count === 'number' && (
            <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white">{count}</span>
          )}
        </h3>
        <span className="text-xs font-bold text-slate-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div className="mt-4">{children}</div>}
    </section>
  );
};

const AdminGhlChecklist: React.FC = () => {
  const c = ghlCounts();
  const tags = ghlAllTags();
  const stages = ghlAllStages();

  const stat = (label: string, value: number, cls: string) => (
    <div key={label} className={`rounded-xl px-4 py-3 ring-1 ${cls}`}>
      <p className="text-2xl font-extrabold">{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wide opacity-80">{label}</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-lg font-extrabold text-slate-900">GoHighLevel implementation checklist</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
          Everything the platform already pushes to the CRM, and everything that still has to be built by hand inside
          GoHighLevel. Tag and stage lists on this page are generated from the same map the live capture function uses,
          so they cannot go stale.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stat('Live in code', c.done, 'bg-emerald-50 text-emerald-800 ring-emerald-200')}
          {stat('Configure in GHL', c.manual, 'bg-amber-50 text-amber-800 ring-amber-200')}
          {stat('Not built yet', c.todo, 'bg-slate-100 text-slate-700 ring-slate-200')}
          {stat('Auto tags', c.tags, 'bg-sky-50 text-sky-800 ring-sky-200')}
        </div>
      </div>

      <Card id="ghl-done" title="1. Integrations already completed" Icon={CheckCircle2} count={GHL_COMPLETED.length}>
        <ul className="space-y-3">
          {GHL_COMPLETED.map((i) => (
            <ItemRow key={i.title} item={i} />
          ))}
        </ul>
      </Card>

      <Card id="ghl-workflows" title="2. Workflows to create in GoHighLevel" Icon={Workflow} count={c.workflows}>
        <ul className="space-y-3">
          {GHL_WORKFLOWS.map((w) => (
            <li key={w.name} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-900">{w.name}</p>
                  <p className="text-xs font-semibold text-slate-500">
                    {w.audience} · {w.channel === 'both' ? 'email + SMS' : w.channel}
                  </p>
                </div>
                <StatusPill status={w.status} />
              </div>
              <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-600">
                Trigger: {w.trigger}
              </p>
              <ul className="mt-2 space-y-1">
                {w.steps.map((s) => (
                  <li key={s} className="flex gap-2 text-sm text-slate-600">
                    <span className="text-slate-300">—</span>
                    {s}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Card>

      <Card id="ghl-pipelines" title="3. Recommended pipelines" Icon={GitBranch} count={c.pipelines}>
        <div className="grid gap-3 lg:grid-cols-2">
          {GHL_PIPELINES.map((p) => (
            <div key={p.name} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-bold text-slate-900">{p.name}</p>
              <p className="mt-1 text-sm text-slate-600">{p.purpose}</p>
              <p className="mt-2 font-mono text-[11px] text-slate-400">Creates opportunity on: {p.entry}</p>
              <ol className="mt-2 space-y-1">
                {p.stages.map((s, i) => (
                  <li key={s} className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </Card>

      <Card id="ghl-tags" title="4. Tags applied automatically" Icon={Tag} count={tags.length}>
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tg) => (
            <span
              key={tg}
              className="rounded-full bg-white px-2.5 py-1 font-mono text-[11px] text-slate-700 ring-1 ring-slate-200"
            >
              {tg}
            </span>
          ))}
        </div>
        <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500">Generated per contact</p>
        <ul className="mt-2 space-y-1.5">
          {GHL_DYNAMIC_TAGS.map((d) => (
            <li key={d.pattern} className="text-sm text-slate-600">
              <span className="font-mono text-[11px] text-slate-900">{d.pattern}</span> — {d.detail}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs font-bold uppercase tracking-wide text-slate-500">Pipeline stages emitted</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {stages.map((s) => (
            <span key={s} className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white">
              {s}
            </span>
          ))}
        </div>
      </Card>

      <Card id="ghl-fields" title="5. Custom fields synchronized" Icon={Database} count={c.fields}>
        <ul className="space-y-1.5">
          {GHL_CUSTOM_FIELDS.map((f) => (
            <li key={f.key} className="rounded-lg bg-white px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200">
              <span className="font-mono text-[11px] font-bold text-slate-900">{f.key}</span> — {f.detail}
            </li>
          ))}
        </ul>
      </Card>

      <Card id="ghl-email" title="6. Email automations" Icon={Mail} count={GHL_EMAIL_ACTIVE.length}>
        <ul className="space-y-3">
          {GHL_EMAIL_ACTIVE.map((i) => (
            <ItemRow key={i.title} item={i} />
          ))}
        </ul>
      </Card>

      <Card id="ghl-sms" title="7. SMS automations" Icon={MessageSquare} count={GHL_SMS_ACTIVE.length}>
        <ul className="space-y-3">
          {GHL_SMS_ACTIVE.map((i) => (
            <ItemRow key={i.title} item={i} />
          ))}
        </ul>
      </Card>

      <Card id="ghl-lists" title="8. Smart lists" Icon={ListFilter} count={c.lists}>
        <ul className="space-y-1.5">
          {GHL_SMART_LISTS.map((l) => (
            <li key={l.name} className="rounded-lg bg-white px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200">
              <span className="font-bold text-slate-900">{l.name}</span> — {l.filter}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

export default AdminGhlChecklist;
